import React, { useState } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import {
  Inventory2 as BulkIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Check as CheckIcon,
  RocketLaunch as RocketLaunchIcon,
} from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { COLORS } from '../utils/constants';
import SafetyGuidelines from './SafetyGuidelines';
import BulkBookingPromo from './BulkBookingPromo';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Mobile Floating Button - Expands to show Bulk Booking + Safety Guidelines
 */
export default function MobilePromoFloat() {
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleBulkClick = () => {
    router.push('/bulk-booking');
    handleClose();
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 285, // Above bottom nav
          right: 20,
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
          zIndex: 1000,
          '&:hover': {
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            boxShadow: '0 6px 16px rgba(245, 158, 11, 0.5)',
          },
        }}
      >
        <RocketLaunchIcon sx={{ fontSize: 24 }} />
      </Fab>

      {/* Expanded Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '85vh',
            overflowY: 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 800,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BulkIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t('estimate.mobile.shipSmarter')}
            </Typography>
          </Box>
          <Button onClick={handleClose} sx={{ minWidth: 'auto', p: 0.5 }}>
            <CloseIcon />
          </Button>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Bulk Booking Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BulkIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  📦 {t('estimate.bulk.title')}
                </Typography>
              </Box>
              <Stack spacing={1.5} sx={{ pl: 0.5 }}>
                {[
                  {
                    titleKey: 'estimate.bulk.saveTime',
                    descKey: 'estimate.bulk.saveTimeDesc',
                  },
                  {
                    titleKey: 'estimate.bulk.getDiscounts',
                    descKey: 'estimate.bulk.getDiscountsDesc',
                  },
                  {
                    titleKey: 'estimate.bulk.businessContracts',
                    descKey: 'estimate.bulk.businessContractsDesc',
                  },
                ].map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        minWidth: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#fbbf24',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 800,
                          color: '#fff',
                        }}
                      >
                        ✓
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: COLORS.textPrimary,
                          fontSize: '0.9rem',
                        }}
                      >
                        {t(item.titleKey)}
                      </Typography>
                      <Typography
                        sx={{
                          color: COLORS.textSecondary,
                          fontSize: '0.75rem',
                          mt: 0.25,
                        }}
                      >
                        {t(item.descKey)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Safety Guidelines Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 20, color: COLORS.accent }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t('estimate.safety.title')}
                </Typography>
              </Box>
              <Stack spacing={1.2} sx={{ pl: 0.5 }}>
                {[
                  t('estimate.safety.tip1'),
                  t('estimate.safety.tip2'),
                  t('estimate.safety.tip3'),
                  t('estimate.safety.tip4'),
                  t('estimate.safety.tip5'),
                ].map((guideline, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1 }}>
                    <CheckIcon
                      sx={{
                        fontSize: 14,
                        color: COLORS.accent,
                        mt: 0.3,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {guideline}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} sx={{ flex: 1 }}>
            {t('estimate.mobile.close')}
          </Button>
          <Button
            onClick={handleBulkClick}
            variant="contained"
            startIcon={<BulkIcon />}
            sx={{
              flex: 1,
              background: '#f59e0b',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { background: '#d97706' },
            }}
          >
            {t('estimate.bulk.tryNowShort')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
